import * as React from "react";

// NPM
import clsx from "clsx";
import { animated, useTrail, interpolate, config } from "react-spring";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Button, Grid, Typography } from "@material-ui/core";

// Utility
import Utility from "../../domain/classes/Utility";

// Interfcaes
import IQuestionOption from "../../domain/interfaces/IQuestionOption";
import IPatientDetails from "../../domain/interfaces/IPatientDetails";

// Contexts
import { useQuestionaireManager } from "../../contexts/QuestionaireManager.Context";

// Classes
import Questionaire from "../../domain/classes/Questionaire";

// Utility
import PatientAttributesQuestions from "../../domain/utility/questions";

// Enums
import JourneyStage from "../../domain/enums/JourneyStage";
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";
import IAnimatable from "../../domain/interfaces/IAnimatable";

export const styles = (theme: Theme) =>
	createStyles({
		root: {
			paddingTop: theme.spacing(16),
		},
		stepper: {},
	});

export interface GenericQuestionContainerProps
	extends React.HTMLAttributes<HTMLDivElement>,
		IAnimatable {}

const GenericQuestionContainer = React.forwardRef<
	HTMLDivElement,
	GenericQuestionContainerProps & WithStyles<typeof styles>
>(({ classes, className, hasAnimation, ...divProps }, ref) => {
	// Contexts
	const {
		currentQuestion,
		handleCurrentQuestionChange,
	} = useQuestionaireManager();

	const {
		handleJourneyStageChange,
		handlePatientDetailsChange,
	} = useApplicationManager();

	// Handlers
	const handleOptionClick = (option: IQuestionOption<IPatientDetails>) => {
		handlePatientDetailsChange(option.key, option.value);

		if (option.nextId === "end") {
			handleJourneyStageChange(JourneyStage.AppointmentTimeSelection);
		} else {
			const nextQuestion = Questionaire.getNext(
				PatientAttributesQuestions,
				option,
				currentQuestion
			);
			if (nextQuestion) handleCurrentQuestionChange(nextQuestion);
		}
	};

	const goBack = () => {
		const prevQuestion = Questionaire.getPrev(
			PatientAttributesQuestions,
			currentQuestion
		);

		if (prevQuestion) handleCurrentQuestionChange(prevQuestion);
	};

	// Animation springs
	const trail = useTrail(currentQuestion.options.length, {
		config: config.wobbly,
		opacity: 1,
		y: 0,
		from: { opacity: 0, y: 20 },
		reset: true,
	});

	const currentOptions = [
		...currentQuestion.options,
	].sort(
		(
			a: IQuestionOption<IPatientDetails>,
			b: IQuestionOption<IPatientDetails>
		) => Utility.sortByAttribute(a, b, "color")
	);

	return (
		<div className={clsx(classes.root, className)} {...divProps} ref={ref}>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Typography align="center" variant="h5">
						<strong>{currentQuestion.question}</strong>
					</Typography>
				</Grid>

				{hasAnimation
					? trail.map(({ opacity, y, ...rest }, index) => {
							const opt = currentOptions[index];

							const AnimatedGrid = animated(Grid);

							return (
								<AnimatedGrid
									item
									xs={12}
									key={opt.value.toString()}
									style={{
										opacity,
										transform: interpolate(
											[y],
											(y) => `translateY(${y}px)`
										),
										...rest,
									}}
								>
									<Button
										fullWidth
										variant="contained"
										color={opt.color}
										onClick={() => {
											handleOptionClick(opt);
										}}
									>
										{opt.text}
									</Button>
								</AnimatedGrid>
							);
					  })
					: currentOptions.map(
							(opt: IQuestionOption<IPatientDetails>) => {
								return (
									<Grid
										item
										xs={12}
										key={opt.value.toString()}
									>
										<Button
											fullWidth
											variant="contained"
											color={opt.color}
											onClick={() => {
												handleOptionClick(opt);
											}}
										>
											{opt.text}
										</Button>
									</Grid>
								);
							}
					  )}

				{currentQuestion.prevId && (
					<Grid item xs={12}>
						<Button fullWidth onClick={goBack}>
							Back
						</Button>
					</Grid>
				)}
			</Grid>
		</div>
	);
});

const StyledGenericQuestionContainer = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("GenericQuestionContainer"),
})(GenericQuestionContainer);

export default StyledGenericQuestionContainer;

export const AnimatedGenericQuestionContainer = animated(
	StyledGenericQuestionContainer
);
